/**
 * Database 模块封装（#89098）
 *
 * 数据面 `/.cloud/database/rest/**`，PostgREST 语义（设计文档 §6.1）。
 *
 * ============================================================================
 * 为什么有这层 facade，而不是把 vendor 的 PostgrestClient 裸抛给应用
 *
 * 其余三个模块（auth/storage/llm）都在 SDK 侧收一层 facade，公开自命名类型、
 * 藏住 vendor —— database 若独独裸透传，会有两个后果：
 *
 *  1. vendor 类型泄漏。`index.ts` 明确规定不导出 `PostgrestClient` 之类的名字
 *     （泄漏后消费方会写出依赖第三方命名的代码，之后换不掉）。裸抛就等于把
 *     整个 vendor 类型面暴露到公开 d.ts。
 *  2. 入口失控。vendor 的 `.schema()` 能切到任意 schema，但数据面只放行 public
 *     （网关 `provider/tcb/data.go` 的 deniedSchemas 会拒 auth、pg_ 前缀、information_schema）。
 *     裸抛会让应用写出「类型上成立、运行期必被网关拒」的代码。
 *
 * 因此这里收一层**薄** facade：只转发 `from` / `rpc`（保留 vendor builder 的
 * 全部链式与 `{data,error}` 语义，体验对齐 supabase-js），去掉 `.schema()`，
 * 并以自己的泛型入口 `Database` 承接强类型（对齐 supabase 的 createClient<DB>）。
 *
 * 这里**没有自研请求层**，底座仍是 `src/vendor/postgrest`。原因：
 *  - 官方 `@cloudbase/js-sdk` 用不了 —— `getBaseEndPoint` 会把配置值截取到 host，
 *    我们的 `/.cloud/database/rest` 前缀被丢弃（`runtime-auth-design.md` §4.1）。
 *  - vendor 的客户端只把 base URL 当字符串保存，路径原样保留，正是所需行为。
 * ============================================================================
 */

import { CLOUD_MODULE_PATHS } from '../paths.js';
import type { CloudRuntimeConfig } from '../types.js';
import { PostgrestClient } from '../vendor/postgrest/index.js';

/**
 * WorkBuddy 数据库模块。PostgREST 语义、`{ data, error }` 信封，
 * 查询构造器与 supabase-js 的 `.from()` 完全一致。
 *
 * 泛型 `Database` 承接 [类型生成器](https://supabase.com/docs/guides/api/rest/generating-types)
 * 产出的 schema 类型，用法与 supabase 的 `createClient<Database>()` 一致；
 * 不传时退化为无类型（`any` Row），运行期行为不变。
 */
export class WorkBuddyDatabaseModule<Database = unknown> {
    /**
     * 底层 PostgREST 客户端。刻意保持 private —— 对外只暴露 `from` / `rpc`，
     * 不让 vendor 客户端（及其 `.schema()` 等入口）泄漏到应用。
     */
    private readonly client: PostgrestClient<Database>;

    constructor(config: CloudRuntimeConfig, fetch: typeof globalThis.fetch) {
        // 身份由 `fetch` 携带（publishableKey + 自签 session），所以这里不传 headers ——
        // 避免出现第二处鉴权注入点。
        this.client = new PostgrestClient<Database>(
            `${config.endpoint}${CLOUD_MODULE_PATHS.database}`,
            { fetch },
        );

        // 直接把 vendor 的方法引用绑定过来，保留其全部重载与泛型签名
        // （用 Parameters/ReturnType 包一层会塌掉重载、弱化类型）。
        // 这是「薄 facade」：只搬 from / rpc 两个入口，不搬 .schema() 等。
        this.from = this.client.from.bind(this.client);
        this.rpc = this.client.rpc.bind(this.client);
    }

    /**
     * 在一张表或视图上发起查询。返回值即 supabase-js 的查询构造器，
     * 支持 `select/insert/upsert/update/delete` 及全部 filter/transform 链，
     * `await` 得到 `{ data, error }`。语义与 supabase-js `.from()` 一致。
     *
     * 只放行 public schema —— 不提供 `.schema()` 切换（数据面会拒系统 schema）。
     */
    readonly from: PostgrestClient<Database>['from'];

    /**
     * 调用一个 PostgreSQL 函数（RPC）。语义同 supabase-js `.rpc()`：
     * 返回可继续 `.select()/.order()/.limit()` 的构造器（集合返回型函数）。
     */
    readonly rpc: PostgrestClient<Database>['rpc'];

    /**
     * 归一化后的数据面基址（`/.cloud/database/rest`，无尾斜杠）。
     * 仅用于诊断/契约测试；应用不应据此手拼请求。
     */
    get url(): string {
        return this.client.url;
    }
}

/**
 * 创建 Database 模块。
 */
export function createDatabaseModule<Database = unknown>(
    config: CloudRuntimeConfig,
    fetch: typeof globalThis.fetch,
): WorkBuddyDatabaseModule<Database> {
    return new WorkBuddyDatabaseModule<Database>(config, fetch);
}
